import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, TrendingUp, Users, Globe } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { productionLogger } from "@/utils/production-logger";

interface SubscriberStats {
  total_subscribers: number;
  total_unsubscribed: number;
  unique_domains: number;
  recent_subscriptions_24h: number;
  recent_subscriptions_7d: number;
  sources_breakdown: Record<string, number>;
}

export function SubscriberStatsWidget() {
  const [stats, setStats] = useState<SubscriberStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const fetchSubscriberStats = async () => {
      try {
        const { data, error } = await supabase.rpc('get_secure_subscriber_statistics');
        
        if (error) throw error;
        
        if (mounted) {
          setStats(data as unknown as SubscriberStats);
          setError(null);
        }
      } catch (err) {
        productionLogger.error('Failed to fetch subscriber stats:', err);
        if (mounted) {
          setError('Unable to load subscriber data');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchSubscriberStats();

    // Set up real-time subscription for new subscribers
    const channel = supabase
      .channel('subscriber_stats_widget')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscribers'
        },
        () => {
          fetchSubscriberStats();
        }
      )
      .subscribe();

    // Refresh every 60 seconds
    const interval = setInterval(fetchSubscriberStats, 60000);

    return () => {
      mounted = false;
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, []);

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Subscribers
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            <div className="h-8 bg-muted rounded w-16"></div>
            <div className="h-4 bg-muted rounded w-24"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Mail className="w-4 h-4 text-destructive" />
            Subscribers
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-destructive text-sm">{error}</div>
        </CardContent>
      </Card>
    );
  }

  const totalActive = stats?.total_subscribers || 0;
  const recent24h = stats?.recent_subscriptions_24h || 0;
  const recent7d = stats?.recent_subscriptions_7d || 0;
  const uniqueDomains = stats?.unique_domains || 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Mail className="w-4 h-4 text-primary" />
          Subscribers
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <div className="text-2xl font-bold text-primary">
              {totalActive.toLocaleString()}
            </div>
            {recent24h > 0 && (
              <div className="flex items-center gap-1 text-xs text-primary">
                <TrendingUp className="w-3 h-3" />
                +{recent24h}
              </div>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            Active subscribers
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              {recent7d} this week
            </div>
            <div className="flex items-center gap-1">
              <Globe className="w-3 h-3" />
              {uniqueDomains} domains
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}